import React from "react";
import { BsShareFill } from "react-icons/bs";
import { MdDownload } from "react-icons/md";
import { ShareButton } from "@npm_leadtech/share-button";
import { DownloadButton } from "@npm_leadtech/downloader-button";

import { Link } from "@/components/Link";
import { Lazy } from "@/components/Lazy";
import { IconList } from "@/components/IconList";
import { Typography } from "@/components/Typography";

import { PreviewLayout } from ".";

import { QREditorContext } from "@/contexts/QREditorContext";

import { checkValidURL } from "@/utils/formHelper";

import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";

export default function PreviewMp3(props: QRProps<"mp3">) {
  const qrEditorContext = React.useContext<QREditorContextProps<"mp3">>(QREditorContext);
  const data = props?.data || qrEditorContext?.qrData.data;
  const isInitPreview = props?.initPreview;

  const showQrContentWrapper = data?.logoUrl || data?.mp3Title || data?.mp3Description || data?.mp3FileUrl;

  const renderShareButton = () => {
    if (isInitPreview) {
      return <BsShareFill style={{ color: data?.themeColor?.buttonColor }}/>
    }

    if (data?.mp3FileUrl) {
      return <ShareButton 
        color={data?.themeColor?.buttonColor} 
        icon={<BsShareFill />} 
      />;
    }
  }

  const renderDownloadButton = () => {
    if (isInitPreview) {
      return <div className="download-button">
        <MdDownload style={{ color: data?.themeColor?.buttonColor }} />
      </div> 
    }
    
    if (data?.mp3FileUrl && data?.allowDownload) {
      return <DownloadButton 
        downloadSource={[data?.mp3FileUrl]} 
        disabled={!data?.mp3FileUrl}  
        className="download-button"
        customButton={<MdDownload style={{ color: data?.themeColor?.buttonColor }} />} 
      />;
    }
  }

  return (
    <PreviewLayout
      page="mp3"
      dataQA="preview-mp3-container"
      backgroundColor={data?.themeColor?.backgroundColor}
      includeBrowser
      isInitPreview={isInitPreview}
    >
      <div className="qr-content">
        <div className="mp3-header">
          {renderDownloadButton()}
          {renderShareButton()}
        </div>
        {
          showQrContentWrapper && (
            <div className="mp3-info">
              {
                data?.logoUrl && (
                  <Lazy placeholderClass="skeleton-placeholder__audio__image">
                    <img src={data?.logoUrl} alt="audio image" />
                  </Lazy>
                )
              }
              <Typography
                Tag="p"
                classname="u-semibold u-spacing-0 u-spacing-4--top"
                size="l"
                style={{ fontSize: "1rem", lineHeight: "1.364rem" }}
              >
                {data?.mp3Title}
              </Typography>
              <Typography
                Tag="p"
                classname=""
                style={{ lineHeight: "1.125rem", letterSpacing: "-0.2px", fontSize: "0.75rem" }}
                spacing={4}
                size="s"
              >
                {data?.mp3Description}
              </Typography>
              {data?.mp3FileUrl ?
                <div key={data?.mp3FileName} className="audio-wrapper">
                  <audio controls controlsList={!data?.allowDownload ? "nodownload" : undefined} preload="auto">
                    <source src={data?.mp3FileUrl} type="audio/mpeg" />
                    <p>Your browser does not support HTML5 audio. Here is a <a href={data?.mp3FileUrl}>link to the audio</a> instead.</p>
                  </audio>
                </div>
                : isInitPreview
                  ? <div>
                      <img src="/images/phone-previews/defaultPreview/audio-controls.png" alt="audio controls" />
                    </div>
                  : null
              } 
            </div>
          )
        }
        {data?.mp3ButtonText && (
          <Link
            externalLink
            className="button u-spacing-4--top u-flex u-flex--center"
            href={checkValidURL(data?.mp3ButtonUrl) || ""}
            style={{
              backgroundColor: data?.themeColor?.buttonColor,
              height: "40px",
            }}
          >
            <span data-testid="button-label" className="button__text body-s">
              {data?.mp3ButtonText}
            </span>
          </Link>
        )}

        {data?.socialNetworks?.length > 0 ? (
          <div className="IconList-wrapper">
            <IconList center items={data?.socialNetworks} />
          </div>
        ) : (
          isInitPreview 
            ? <div className="initPreview-logos">
                {data?.socialLogos?.map((socialLogo) => (
                  <Lazy
                    key={socialLogo}
                    placeholderClass="skeleton-placeholder__links__logo"
                  >
                    <img key={socialLogo} src={socialLogo} alt="pdf menu preview" />
                  </Lazy>
                ))}
              </div>
            : <div style={{ width: "100%", height: "1px", marginTop: "2.5rem" }}></div>
        )}
      </div>
    </PreviewLayout>
  );
}